import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle} from '@/components/ui/modal-atoms/ModalAtoms'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {InputSelectReportDateByPatient} from '@/features/medical-reports/components/input-select-report-date-by-patient/InputSelectReportDateByPatient'
import {InputSelectReportTypeByPatient} from '@/features/medical-reports/components/input-select-report-type-by-patient/InputSelectReportTypeByPatient'
import {MedicalReportDateByProject, MedicalReportTypeByProject} from '@/features/medical-reports/types'
import {InputSelectUserByPatient} from '@/features/users/components/input-select-user-by-patient/InputSelectUserByPatient'
import {UserByPatient} from '@/features/users/types'
import {useTranslation} from '@/translations/i18n'
import {useState} from 'react'

export const MedicalRecordAggregatedPrintFinderModal = ({
    patientId,
    closeModal,
    isLoading,
    onConfirm
}: {
    patientId: string
    closeModal: () => void
    isLoading: boolean
    onConfirm: (
        user: UserByPatient,
        reportType: MedicalReportTypeByProject,
        date: MedicalReportDateByProject['date']
    ) => void
}) => {
    const {t} = useTranslation()
    const [selectedUser, setSelectedUser] = useState<UserByPatient | null>(null)
    const [selectedReportType, setSelectedReportType] = useState<MedicalReportTypeByProject | null>(null)
    const [selectedDate, setSelectedDate] = useState<MedicalReportDateByProject['date'] | null>(null)

    const handleConfirm = () => {
        if (!selectedUser || !selectedReportType || !selectedDate) return
        onConfirm(selectedUser, selectedReportType, selectedDate)
    }

    return (
        <Modal width="400px" overflowY="unset">
            <ModalHeader>
                <Flexbox fullWidth justify="space-between" align="center">
                    <ModalTitle>{t('medical_records:print_aggregate_reports')}</ModalTitle>
                    <Button shape="square" variant="ghost" onClick={closeModal}>
                        <XCloseIcon />
                    </Button>
                </Flexbox>
            </ModalHeader>
            <ModalBody>
                <InputSelectUserByPatient
                    patientId={patientId}
                    value={selectedUser ?? null}
                    label={t('commons:operator')}
                    onChange={user => (!Array.isArray(user) ? setSelectedUser(user) : null)}
                    placeholder={t('commons:select')}
                />

                {selectedUser && (
                    <InputSelectReportTypeByPatient
                        label={t('medical_records:report_type')}
                        patientId={patientId}
                        userId={selectedUser.id}
                        value={selectedReportType ?? null}
                        onChange={reportType => setSelectedReportType(reportType ?? null)}
                        placeholder={t('commons:select')}
                    />
                )}

                {selectedUser && selectedReportType && (
                    <InputSelectReportDateByPatient
                        label={t('medical_records:report_date')}
                        patientId={patientId}
                        userId={selectedUser.id}
                        medicalRecordReportTypeId={selectedReportType.id}
                        value={selectedDate ?? null}
                        onChange={value => setSelectedDate(value?.date ?? null)}
                        placeholder={t('commons:select')}
                    />
                )}
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={closeModal}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    variant="primaryNeutral"
                    disabled={isLoading || !selectedUser || !selectedReportType || !selectedDate}
                    onClick={handleConfirm}
                >
                    {t('commons:print')}
                    {isLoading && <Spinner />}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
